import { AcademicCapIcon } from '@heroicons/react/24/solid';
import { Card, CardBody, CardHeader } from '@heroui/react';
import { type FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { SpecialtiesSelect } from '@/app/components/specialties-select.tsx';
import toast from '@/app/toast.ts';
import type { AccountModel } from '@/app/types/account.type.ts';
import type { AccountSpecialtyModel } from '@/app/types/specialty.type.ts';
import {
  useDeleteAccountSpecialtyMutation,
  usePostAccountSpecialtyMutation,
} from '@/slices/account-api-slice.ts';

export interface ManageSpecialtiesProps {
  account: AccountModel;
}

export const ManageSpecialties: FC<ManageSpecialtiesProps> = ({ ...props }) => {
  const form = useForm<{ specialties: AccountSpecialtyModel[] }>({
    values: {
      specialties: props?.account?.specialties,
    },
  });

  const [postSpecialty, { isError: postError }] = usePostAccountSpecialtyMutation();
  const [deleteSpecialty, { isError: deleteError }] = useDeleteAccountSpecialtyMutation();

  useEffect(() => {
    if (postError || deleteError) {
      toast.error(
        `An error occurred while attempting to ${postError ? 'add' : 'delete'} the specialty.`,
      );
    }
  }, [postError, deleteError]);

  const handleAdd = (value: AccountSpecialtyModel) => {
    value.accountId = props.account.id;
    postSpecialty(value);
  };

  const handleDelete = (value: AccountSpecialtyModel) => {
    deleteSpecialty({ accountKey: props.account.key, id: value.id });
  };

  return (
    <Card>
      <CardHeader className='flex gap-2 items-center px-4 pt-4'>
        <AcademicCapIcon className='size-5' aria-hidden='true' />
        <h2 className='font-semibold'>Specialties</h2>
      </CardHeader>
      <CardBody className='px-4 pb-4'>
        <FormProvider {...form}>
          <form>
            <SpecialtiesSelect
              accountId={props.account?.id}
              title={null}
              name='specialties'
              onAdd={handleAdd}
              onRemove={handleDelete}
            />
          </form>
        </FormProvider>
      </CardBody>
    </Card>
  );
};
